import {
  Box,
  Flex,
  Grid,
  HStack,
  Spinner,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RiAddCircleLine } from 'react-icons/ri';
import { MdOutlineSmartToy, MdPerson } from 'react-icons/md';
import { FaGlobe, FaFolder } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DigitalTwin,
  DtType,
  ProposalType,
  useDigitalTwinsQuery,
} from '../../generated/graphql';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { SearchInput } from '../helivan/SearchInput';
import { HelivanLoading } from '../HelivanLoading';
import { RequestModal } from '../Requests/RequestModal';
import { DigitalTwinCard } from './DigitalTwinCard';
import { capitalize } from '../../utils/capitalize';
import ProjectFilter from '../Projects/ProjectFilter';
import { MyAgents } from '../MyAgents/MyAgents';
import { CollectionsView } from '../MyAgents/CollectionsView';
import { ConnectAgentModal } from '../MyAgents/ConnectAgentModal';
import { useAuth } from '../../contexts/AuthContext';

export const DigitalTwins = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Signed-out visitors only see the public Personas tab (My Agents is user data).
  const { isAuthenticated } = useAuth();

  // Determine initial tab based on URL - default to My Agents (index 1)
  const isPersonasRoute = location.pathname === '/personas';
  const [tabIndex, setTabIndex] = useState(isPersonasRoute ? 0 : 1);
  const [personaSubTabIndex, setPersonaSubTabIndex] = useState(0);

  // Update tab when route changes (anonymous users are pinned to Personas).
  useEffect(() => {
    if (location.pathname === '/personas' || !isAuthenticated) {
      setTabIndex(0);
    } else {
      setTabIndex(1);
    }
  }, [location.pathname, isAuthenticated]);

  const handleTabChange = (index: number) => {
    setTabIndex(index);
    if (index === 0) {
      navigate('/personas');
    } else {
      navigate('/agents');
    }
  };

  // Pagination state
  const [offset, setOffset] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const ITEMS_PER_PAGE = 20;

  // State to track initial loading vs. loading more
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  // Filter state
  const [selectedTypes, setSelectedTypes] = useState<DtType[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Query with pagination parameters
  const { data, loading, fetchMore } = useDigitalTwinsQuery({
    variables: {
      limit: ITEMS_PER_PAGE,
      offset: 0,
      search: searchTerm,
      typeFilter: selectedTypes.length > 0 ? selectedTypes : undefined,
    },
  });

  // Track all loaded digital twins
  const [allLoadedTwins, setAllLoadedTwins] = useState<DigitalTwin[]>([]);

  // Modal state
  const requestNew = useDisclosure();
  const createAgentModal = useDisclosure();

  // Reference to scrollable container
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<boolean>(false);

  // Update allLoadedTwins when initial data loads
  useEffect(() => {
    if (data?.getAllDigitalTwins?.twins) {
      setAllLoadedTwins(data.getAllDigitalTwins.twins as DigitalTwin[]);
      setHasMore(
        data.getAllDigitalTwins.twins.length <
        data.getAllDigitalTwins.totalCount
      );
      setOffset(data.getAllDigitalTwins.twins.length);
    }
  }, [data]);

  // Reset data and fetch new results when search/filter params change
  useEffect(() => {
    // Skip on initial render
    if (allLoadedTwins.length === 0 && !data) return;

    // Reset pagination
    setOffset(0);

    // Refetch with new parameters
    fetchMore({
      variables: {
        limit: ITEMS_PER_PAGE,
        offset: 0,
        search: searchTerm,
        typeFilter: selectedTypes.length > 0 ? selectedTypes : undefined,
      },
      updateQuery: (_, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.getAllDigitalTwins) {
          return { getAllDigitalTwins: { twins: [], totalCount: 0 } };
        }

        setAllLoadedTwins(fetchMoreResult.getAllDigitalTwins.twins as DigitalTwin[]);
        setHasMore(
          fetchMoreResult.getAllDigitalTwins.twins.length <
          fetchMoreResult.getAllDigitalTwins.totalCount
        );

        return fetchMoreResult;
      }
    });
  }, [searchTerm, selectedTypes, fetchMore]);

  // Load more data when user scrolls
  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore || isLoadingMore) return;

    loadingRef.current = true;
    setIsLoadingMore(true);
    try {
      const result = await fetchMore({
        variables: {
          offset,
          limit: ITEMS_PER_PAGE,
          search: searchTerm,
          typeFilter: selectedTypes.length > 0 ? selectedTypes : undefined,
        },
        updateQuery: (
          prev: any,
          { fetchMoreResult }: { fetchMoreResult?: any }
        ) => {
          if (!fetchMoreResult || !fetchMoreResult.getAllDigitalTwins) {
            return prev;
          }

          // Append the new page, de-duplicating by id (guards against an
          // overlapping/repeated fetch producing duplicate React keys).
          const seen = new Set<string>();
          const twins = [
            ...prev.getAllDigitalTwins.twins,
            ...fetchMoreResult.getAllDigitalTwins.twins,
          ].filter((t: any) => (seen.has(t.id) ? false : (seen.add(t.id), true)));

          return {
            getAllDigitalTwins: {
              __typename: prev.getAllDigitalTwins.__typename,
              twins,
              totalCount: fetchMoreResult.getAllDigitalTwins.totalCount,
            },
          };
        },
      });

      // The merged result lands in the watched query's `data`; the [data] effect
      // is the single source of truth that advances allLoadedTwins/offset/hasMore.
      // (Previously this block overwrote the list with the page-only `result.data`,
      // resetting offset to the page size so scrolling never advanced.)
      void result;
    } catch (error) {
      console.error('Error loading more twins:', error);
    } finally {
      loadingRef.current = false;
      setIsLoadingMore(false);
    }
  }, [fetchMore, offset, hasMore, isLoadingMore, searchTerm, selectedTypes]);

  // Handle scroll events
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

    // Load more when user reaches near the bottom (within 300px)
    if (
      scrollHeight - scrollTop - clientHeight < 300 &&
      !isLoadingMore &&
      hasMore
    ) {
      loadMore();
    }
  }, [loadMore, isLoadingMore, hasMore]);

  // Attach scroll listener
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Filter personas by type and search term
  const filteredTwins = useMemo(() => {
    let filtered = allLoadedTwins;

    // Filter by type
    if (selectedTypes.length > 0) {
      filtered = filtered.filter((dt) => selectedTypes.includes(dt.type));
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const lowerCaseSearch = searchTerm.toLowerCase();
      filtered = filtered.filter((dt) =>
        dt.name.toLowerCase().includes(lowerCaseSearch)
      );
    }

    return filtered;
  }, [allLoadedTwins, selectedTypes, searchTerm]);

  const isInitialLoading = loading && allLoadedTwins.length === 0;

  return (
    <Stack width="full" height="full" spacing={0} overflow="hidden">
      <RequestModal
        prefill={ProposalType.DigitalTwin}
        isOpen={requestNew.isOpen}
        onClose={requestNew.onClose}
      />

      {/* Signed-out visitors get the lightweight skill.md connect flow (the real
          CreateAgentModal lives in the My Agents tab, which they can't see). */}
      {!isAuthenticated && (
        <ConnectAgentModal isOpen={createAgentModal.isOpen} onClose={createAgentModal.onClose} />
      )}

      {/* Header - full width */}
      <Flex
        width="full"
        alignItems="center"
        py="12px"
        px="24px"
        minHeight="64px"
        bg={HelivanColor.background.primary}
        borderBottom="1px solid"
        borderBottomColor={HelivanColor.border.primary}
        justifyContent="space-between"
        gap={4}
        flexShrink={0}
      >
        {/* Left: Title and tabs */}
        <HStack spacing={6} flex="0 0 auto">
          <HelivanText variant="sectionHeader">Agents</HelivanText>
          <HStack spacing={2}>
            <Box
              onClick={() => handleTabChange(0)}
              px="8px"
              py="4px"
              bg={tabIndex === 0 ? HelivanColor.background.secondary : 'white'}
              border="1px solid"
              borderColor={HelivanColor.border.primary}
              borderRadius="6px"
              _hover={{ bg: HelivanColor.background.secondary }}
              cursor="pointer"
            >
              <HStack spacing={2}>
                <MdOutlineSmartToy size={14} color={HelivanColor.text.blueMid} />
                <HelivanText variant="smallBody">Personas</HelivanText>
              </HStack>
            </Box>
            {isAuthenticated && (
              <Box
                onClick={() => handleTabChange(1)}
                px="8px"
                py="4px"
                bg={tabIndex === 1 ? HelivanColor.background.secondary : 'white'}
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                borderRadius="6px"
                _hover={{ bg: HelivanColor.background.secondary }}
                cursor="pointer"
              >
                <HStack spacing={2}>
                  <MdOutlineSmartToy size={14} color={HelivanColor.text.helivanBlue} />
                  <HelivanText variant="smallBody">My Agents</HelivanText>
                </HStack>
              </Box>
            )}
          </HStack>
          {/* Search: immediately right of the tabs */}
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search…"
          />
        </HStack>

        {/* Right: Filter and action buttons */}
        <HStack spacing={3} flex="0 0 auto">
          {tabIndex === 0 ? (
            <>
              <ProjectFilter
                selectedTypes={selectedTypes}
                setSelectedTypes={setSelectedTypes}
                type="dt"
              />
              {isAuthenticated ? (
                <HelivanButton onClick={requestNew.onOpen} variant="green" size="sm">
                  <HStack spacing={1}>
                    <RiAddCircleLine color="white" size={14} />
                    <HelivanText colorOverride="white" variant="smallBody">
                      Request New
                    </HelivanText>
                  </HStack>
                </HelivanButton>
              ) : (
                <HelivanButton onClick={createAgentModal.onOpen} variant="green" size="sm">
                  <HStack spacing={1}>
                    <RiAddCircleLine color="white" size={14} />
                    <HelivanText colorOverride="white" variant="smallBody">
                      Add Agent
                    </HelivanText>
                  </HStack>
                </HelivanButton>
              )}
            </>
          ) : (
            <HelivanButton data-tour="add-agent-btn" onClick={createAgentModal.onOpen} variant="green" size="sm">
              <HStack spacing={1}>
                <RiAddCircleLine color="white" size={14} />
                <HelivanText colorOverride="white" variant="smallBody">
                  Add Agent
                </HelivanText>
              </HStack>
            </HelivanButton>
          )}
        </HStack>
      </Flex>

      {/* Content area */}
      {tabIndex === 0 ? (
        // Personas tab content with sub-tabs
        isInitialLoading ? (
          <HelivanLoading />
        ) : (
          <Flex
            flex="1"
            overflowY="auto"
            bg={HelivanColor.background.secondary}
            p={6}
            justifyContent="center"
            ref={scrollRef}
          >
            <Stack spacing={4} width="full" maxWidth="1400px" height="fit-content">
              {/* Sub-tabs for Personas section */}
              <HStack spacing={2}>
                <Box
                  onClick={() => setPersonaSubTabIndex(0)}
                  px="8px"
                  py="4px"
                  bg={personaSubTabIndex === 0 ? 'white' : 'transparent'}
                  border="1px solid"
                  borderColor={personaSubTabIndex === 0 ? HelivanColor.border.primary : 'transparent'}
                  borderRadius="6px"
                  _hover={{ bg: 'white' }}
                  cursor="pointer"
                  boxShadow={personaSubTabIndex === 0 ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'}
                >
                  <HStack spacing={2}>
                    <FaGlobe size={14} color={HelivanColor.text.blueMid} />
                    <HelivanText variant="smallBody">Public Personas ({data?.getAllDigitalTwins?.totalCount ?? 0})</HelivanText>
                  </HStack>
                </Box>
                <Box
                  onClick={() => setPersonaSubTabIndex(1)}
                  px="8px"
                  py="4px"
                  bg={personaSubTabIndex === 1 ? 'white' : 'transparent'}
                  border="1px solid"
                  borderColor={personaSubTabIndex === 1 ? HelivanColor.border.primary : 'transparent'}
                  borderRadius="6px"
                  _hover={{ bg: 'white' }}
                  cursor="pointer"
                  boxShadow={personaSubTabIndex === 1 ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'}
                >
                  <HStack spacing={2}>
                    <FaFolder size={14} color={HelivanColor.text.blueMid} />
                    <HelivanText variant="smallBody">Collections</HelivanText>
                  </HStack>
                </Box>
              </HStack>

              {/* Sub-tab content */}
              {personaSubTabIndex === 0 && (
                <>
                  {filteredTwins.length > 0 ? (
                    <Grid
                      templateColumns={[
                        'repeat(2, 1fr)',
                        'repeat(2, 1fr)',
                        'repeat(3, 1fr)',
                        'repeat(3, 1fr)',
                        'repeat(4, 1fr)',
                        'repeat(5, 1fr)',
                      ]}
                      gap={5}
                      width="full"
                    >
                      {filteredTwins.map((dt) => {
                        // Get unique project templates by topic
                        const uniqueTopics = Array.from(
                          new Set((dt.projects ?? []).map(p => p.topicShortened ?? p.topic))
                        );
                        return (
                        <DigitalTwinCard
                          name={dt.name}
                          id={dt.id}
                          key={dt.id}
                          industry={dt.type}
                          projectCount={uniqueTopics.length}
                          projectLabels={uniqueTopics.slice(0, 4).map(
                            (topic, idx) => ({
                              name: topic ?? '',
                              variant:
                                idx === 0
                                  ? 'blue'
                                  : idx === 1
                                    ? 'green'
                                    : idx === 2
                                      ? 'success'
                                      : 'error',
                            })
                          )}
                          roleTitle={
                            dt.congressData
                              ? `${capitalize(dt.congressData.chamber)} in ${dt.congressData.state}`
                              : dt.earlyStageVcData
                                ? dt.earlyStageVcData.title != null
                                  ? `${dt.earlyStageVcData.title} at ${dt.earlyStageVcData.firm}`
                                  : dt.earlyStageVcData.firm
                                : dt.fortuneCeoData
                                  ? dt.fortuneCeoData.company
                                  : dt.fedMemberData
                                    ? dt.fedMemberData.title
                                    : undefined
                          }
                        />
                      )})}
                    </Grid>
                  ) : (
                    <Flex justifyContent="center" alignItems="center" width="full" py={10}>
                      <HelivanText variant="largeBody">No personas found</HelivanText>
                    </Flex>
                  )}
                  {isLoadingMore && (
                    <Flex justify="center" py={4} width="full">
                      <Spinner size="md" color={HelivanColor.text.blueMid} />
                    </Flex>
                  )}
                </>
              )}

              {personaSubTabIndex === 1 && (
                <CollectionsView searchTerm={searchTerm} />
              )}
            </Stack>
          </Flex>
        )
      ) : (
        // My Agents tab content - wrapped in same container as Helivan Agents
        <Flex
          flex="1"
          overflowY="auto"
          bg={HelivanColor.background.secondary}
          p={6}
          justifyContent="center"
        >
          <MyAgents
            embedded
            externalSearchTerm={searchTerm}
            createModalDisclosure={createAgentModal}
          />
        </Flex>
      )}
    </Stack>
  );
};
